/**
 * Recast GCS upload helper for bulk/estate quote forms.
 * Presign via Base44 gcsPresignUploads, direct PUT to GCS with progress.
 */
(function (global) {
  'use strict';

  var ALLOWED_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'application/pdf',
    'text/plain',
    'text/csv',
    'application/csv',
    'text/comma-separated-values',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/rtf',
    'text/rtf'
  ];
  var EXTENSION_MIME = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    pdf: 'application/pdf',
    txt: 'text/plain',
    csv: 'text/csv',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    odt: 'application/vnd.oasis.opendocument.text',
    ods: 'application/vnd.oasis.opendocument.spreadsheet',
    rtf: 'application/rtf'
  };
  var GENERIC_MIME_TYPES = ['', 'application/octet-stream', 'binary/octet-stream'];
  var MAX_FILE_BYTES = 15 * 1024 * 1024;
  var MAX_FILES = 10;
  var PRESIGN_URL = 'https://recast-trade-go.base44.app/functions/gcsPresignUploads';
  var BATCH_CONTEXT = 'trade-in-quote';
  var TURNSTILE_TIMEOUT_MS = 15000;
  var turnstileWidgetId = null;
  var BRAND_NAME = 'Recast Fly Fishing';

  function getExtension(filename) {
    var parts = (filename || '').split('.');
    if (parts.length < 2) return '';
    return parts.pop().toLowerCase();
  }

  /**
   * Resolve MIME for presign + PUT: prefer browser file.type,
   * else map from filename extension, else application/octet-stream.
   */
  function resolveFileMimeType(file) {
    var reported = (file && file.type) ? String(file.type).trim() : '';
    if (reported) return reported;
    var ext = getExtension(file && file.name);
    if (ext && EXTENSION_MIME[ext]) return EXTENSION_MIME[ext];
    return 'application/octet-stream';
  }

  function resolveAllowedMime(filename, reportedType) {
    var normalized = (reportedType || '').toLowerCase().trim();
    if (normalized && GENERIC_MIME_TYPES.indexOf(normalized) === -1 && ALLOWED_TYPES.indexOf(normalized) !== -1) {
      return normalized;
    }
    var ext = getExtension(filename);
    return EXTENSION_MIME[ext] || null;
  }

  function validateFile(file) {
    var mime = resolveAllowedMime(file.name, file.type || '');
    if (!mime) {
      return {
        valid: false,
        error: '"' + file.name + '" is not allowed for ' + BRAND_NAME + ' quote requests. Use images, PDF, TXT, CSV, Word, Excel, or Google Docs/Sheets exports (ODT/ODS/RTF).'
      };
    }
    if (file.size > MAX_FILE_BYTES) {
      return { valid: false, error: '"' + file.name + '" exceeds the 15 MB limit.' };
    }
    return { valid: true, mime: mime };
  }

  function validateFiles(fileList, currentCount) {
    var files = Array.from(fileList || []);
    var errors = [];
    var valid = [];

    if (currentCount + files.length > MAX_FILES) {
      return {
        valid: [],
        errors: ['You can attach up to ' + MAX_FILES + ' files to your ' + BRAND_NAME + ' quote request.']
      };
    }

    files.forEach(function (file) {
      var result = validateFile(file);
      if (result.valid) {
        valid.push({ file: file, mime: result.mime });
      } else {
        errors.push(result.error);
      }
    });

    return { valid: valid, errors: errors };
  }

  function initTurnstileWidget(container) {
    if (!container || !global.turnstile) return null;
    if (turnstileWidgetId !== null) return turnstileWidgetId;
    if (container.getAttribute('data-turnstile-rendered') === 'true') {
      var existingId = container.getAttribute('data-widget-id');
      if (existingId) {
        turnstileWidgetId = existingId;
        return turnstileWidgetId;
      }
    }
    try {
      // execution:'execute' = do not challenge until turnstile.execute().
      // retry:'never' + refresh-expired:'never' = stop Cloudflare's ~8s
      // failed-challenge retry loop and idle token refreshes.
      turnstileWidgetId = global.turnstile.render(container, {
        sitekey: container.getAttribute('data-sitekey'),
        size: container.getAttribute('data-size') || 'invisible',
        execution: 'execute',
        retry: 'never',
        'refresh-expired': 'never',
        callback: function (token) {
          global.dispatchEvent(new CustomEvent('turnstile-token', { detail: token }));
        },
        'error-callback': function () {
          global.dispatchEvent(new CustomEvent('turnstile-error'));
          return true;
        }
      });
      container.setAttribute('data-turnstile-rendered', 'true');
      container.setAttribute('data-widget-id', String(turnstileWidgetId));
      return turnstileWidgetId;
    } catch (e) {
      return null;
    }
  }

  function getFreshTurnstileToken(container) {
    return new Promise(function (resolve, reject) {
      if (!global.turnstile) {
        reject(new Error(BRAND_NAME + ' security check is not ready. Please refresh and try again.'));
        return;
      }

      var widgetId = initTurnstileWidget(container);
      if (widgetId === null) {
        reject(new Error(BRAND_NAME + ' security check could not start. Please refresh and try again.'));
        return;
      }

      var settled = false;
      var timeoutId = setTimeout(function () {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(BRAND_NAME + ' security check timed out. Please try again.'));
      }, TURNSTILE_TIMEOUT_MS);

      function cleanup() {
        global.removeEventListener('turnstile-token', onToken);
        global.removeEventListener('turnstile-error', onError);
      }

      function onToken(event) {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        cleanup();
        resolve(event.detail);
      }

      function onError() {
        if (settled) return;
        settled = true;
        clearTimeout(timeoutId);
        cleanup();
        reject(new Error(BRAND_NAME + ' security check failed. Please try again.'));
      }

      global.addEventListener('turnstile-token', onToken);
      global.addEventListener('turnstile-error', onError);

      try {
        global.turnstile.reset(widgetId);
        global.turnstile.execute(widgetId);
      } catch (e) {
        clearTimeout(timeoutId);
        cleanup();
        reject(new Error(BRAND_NAME + ' security check failed. Please try again.'));
      }
    });
  }

  function requestPresign(options) {
    var body = {
      security: {
        turnstile_token: options.turnstileToken,
        shop_domain: global.location.hostname
      },
      batch_context: BATCH_CONTEXT,
      quote_mode: options.quoteMode,
      files: options.files.map(function (f) {
        return {
          name: f.name,
          type: resolveFileMimeType(f),
          size: f.size
        };
      })
    };

    if (options.storageBatchId) {
      body.storage_batch_id = options.storageBatchId;
    }

    return fetch(PRESIGN_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json().then(function (data) {
        console.log('Base44 Presign Response:', data);
        if (!response.ok || data.success === false) {
          var message = (data.error && data.error.message) ||
            (BRAND_NAME + ' could not prepare your file upload (' + response.status + ').');
          throw new Error(message);
        }
        return data;
      });
    });
  }

  function putFileToGcs(uploadUrl, file, onProgress) {
    return new Promise(function (resolve, reject) {
      var targetUrl = uploadUrl;
      if (!targetUrl || targetUrl === 'undefined') {
        reject(new Error(BRAND_NAME + ' signed upload URL is missing or undefined.'));
        return;
      }

      var xhr = new XMLHttpRequest();
      xhr.open('PUT', targetUrl);
      // Only Content-Type — Host is set by the browser; no Authorization or x-goog-*.
      // Must match the type sent in the presign files payload.
      xhr.setRequestHeader('Content-Type', resolveFileMimeType(file));

      xhr.upload.onprogress = function (event) {
        if (!onProgress || !event.lengthComputable) return;
        var percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent);
      };

      xhr.onload = function () {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
          return;
        }
        reject(new Error(BRAND_NAME + ' file upload failed (' + xhr.status + ').'));
      };

      xhr.onerror = function () {
        reject(new Error(BRAND_NAME + ' file upload failed due to a network error.'));
      };

      xhr.send(file);
    });
  }

  function presignAndUpload(options) {
    var turnstileContainer = options.turnstileContainer;
    var quoteMode = options.quoteMode;
    var storageBatchId = options.storageBatchId || null;
    var validatedFiles = options.validatedFiles;
    var onProgress = options.onProgress || function () {};
    var onFileStatus = options.onFileStatus || function () {};

    return getFreshTurnstileToken(turnstileContainer)
      .then(function (token) {
        return requestPresign({
          turnstileToken: token,
          quoteMode: quoteMode,
          storageBatchId: storageBatchId,
          files: validatedFiles.map(function (item) {
            var file = item.file;
            return {
              name: file.name,
              type: resolveFileMimeType(file),
              size: file.size
            };
          })
        });
      })
      .then(function (presignResult) {
        var batchId = presignResult.storage_batch_id || presignResult.storageBatchId || null;
        var uploads = presignResult.uploads || presignResult.files || [];
        var uploadPromises = uploads.map(function (item, index) {
          var source = validatedFiles[index];
          if (!source) return Promise.resolve(null);

          var targetUrl = item.uploadUrl || item.url || item.upload_url;
          var fileKey = item.fileKey || item.file_key || item.key || null;

          onFileStatus(index, { uploadStatus: 'uploading', progress: 0 });

          return putFileToGcs(
            targetUrl,
            source.file,
            function (percent) {
              onProgress(index, percent);
              onFileStatus(index, { uploadStatus: 'uploading', progress: percent });
            }
          ).then(function () {
            onFileStatus(index, {
              uploadStatus: 'uploaded',
              progress: 100,
              fileKey: fileKey
            });
            return fileKey;
          }).catch(function (err) {
            onFileStatus(index, {
              uploadStatus: 'error',
              errorMessage: err.message || (BRAND_NAME + ' file upload failed.')
            });
            throw err;
          });
        });

        return Promise.all(uploadPromises).then(function (keys) {
          if (global.turnstile && turnstileWidgetId !== null) {
            try {
              global.turnstile.reset(turnstileWidgetId);
            } catch (e) { /* ignore */ }
          }
          return {
            storageBatchId: batchId,
            fileKeys: keys.filter(Boolean)
          };
        });
      })
      .catch(function (err) {
        if (global.turnstile && turnstileWidgetId !== null) {
          try {
            global.turnstile.reset(turnstileWidgetId);
          } catch (e) { /* ignore */ }
        }
        throw err;
      });
  }

  function buildStoragePayload(attachments, storageBatchId) {
    var keys = (attachments || [])
      .filter(function (a) { return a.uploadStatus === 'uploaded' && a.fileKey; })
      .map(function (a) { return a.fileKey; });

    return {
      storage_batch_id: storageBatchId || null,
      storage_keys: keys
    };
  }

  function attachmentsBlockSubmit(attachments) {
    if (!attachments || !attachments.length) return false;
    return attachments.some(function (a) {
      return a.uploadStatus === 'presigning' ||
        a.uploadStatus === 'uploading' ||
        a.uploadStatus === 'error' ||
        a.uploadStatus === 'pending';
    });
  }

  var api = {
    ALLOWED_TYPES: ALLOWED_TYPES,
    MAX_FILE_BYTES: MAX_FILE_BYTES,
    MAX_FILES: MAX_FILES,
    PRESIGN_URL: PRESIGN_URL,
    BATCH_CONTEXT: BATCH_CONTEXT,
    validateFile: validateFile,
    validateFiles: validateFiles,
    resolveAllowedMime: resolveAllowedMime,
    initTurnstileWidget: initTurnstileWidget,
    getFreshTurnstileToken: getFreshTurnstileToken,
    presignAndUpload: presignAndUpload,
    buildStoragePayload: buildStoragePayload,
    attachmentsBlockSubmit: attachmentsBlockSubmit
  };

  global.RecastGcsUploader = api;

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }
})(typeof window !== 'undefined' ? window : global);
